import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Button } from "devextreme-react/button";
import { TextBox, TextArea } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import Toolbar, { Item } from "devextreme-react/toolbar";
import data from "../../assets/apiRoutes.json";
import { MESSAGES } from "../../common/Messages";
import "./sendBol.css";

/*
 * Driver Bill of Lading Capture - the page a driver opens on their phone (T20260930.0001).
 *
 * Three steps, in the order the driver works: find the load, confirm it is theirs, photograph the signed
 * BOL and send. The camera is reached with a plain file input carrying `capture`, which is what lets this
 * be a web page at all - the business requirement is explicit that nothing may be installed on the
 * drivers' phones, and that rules out a native app or any SDK.
 *
 * Laid out on the same shell as the other data-entry screens (row dataEntryMainPage > main-page >
 * AccordionItemBody, with the title in a Toolbar header), so it scrolls the way every other page does and
 * inherits the theme. An earlier version set its own full-height page, which took it out of the shell's
 * scrolling container and left the bottom of the form unreachable on a phone.
 */

const MAX_PAGES = 10;

// Long edge for a stored page. A bill of lading is printed text, and 2000px reads comfortably while
// turning a 6MB camera photo into a few hundred KB - the difference between a send that completes at a
// loading dock and one that times out.
const MAX_EDGE_PX = 2000;
const JPEG_QUALITY = 0.8;

const MOBILE_BREAKPOINT = 768;

/**
 * Re-encodes a camera photo down to something sendable over a truck-stop connection.
 *
 * Falls back to the original file whenever anything goes wrong: a page larger than we wanted still gets
 * the paperwork to the office, where a page that failed to convert does not.
 */
const downscale = (file) =>
    new Promise((resolve) => {
        try {
            const url = URL.createObjectURL(file);
            const image = new Image();

            image.onload = () => {
                try {
                    const longest = Math.max(image.width, image.height);
                    const scale = longest > MAX_EDGE_PX ? MAX_EDGE_PX / longest : 1;

                    const canvas = document.createElement("canvas");
                    canvas.width = Math.round(image.width * scale);
                    canvas.height = Math.round(image.height * scale);
                    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);

                    canvas.toBlob(
                        (blob) => {
                            URL.revokeObjectURL(url);
                            resolve(blob && blob.size < file.size ? blob : file);
                        },
                        "image/jpeg",
                        JPEG_QUALITY
                    );
                } catch {
                    URL.revokeObjectURL(url);
                    resolve(file);
                }
            };

            image.onerror = () => {
                URL.revokeObjectURL(url);
                resolve(file);
            };

            image.src = url;
        } catch {
            resolve(file);
        }
    });

const formatDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString();
};

const place = (name, city, state) => ({
    name: (name || "").trim(),
    where: [city, state].map((v) => (v || "").trim()).filter(Boolean).join(", "),
});

const SendBol = () => {
    const accessToken = useSelector((state) => state.oidc.user?.access_token);

    const [searchText, setSearchText] = useState("");
    const [load, setLoad] = useState(null);
    const [pages, setPages] = useState([]);
    const [note, setNote] = useState("");

    const [searching, setSearching] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const [myLoads, setMyLoads] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

    const cameraRef = useRef(null);
    const libraryRef = useRef(null);

    useEffect(() => {
        const updateView = () => setIsMobileView(window.innerWidth <= MOBILE_BREAKPOINT);
        window.addEventListener("resize", updateView);
        return () => window.removeEventListener("resize", updateView);
    }, []);

    // Object URLs are revoked only when a page is dropped or the screen is left; revoking on every render
    // would blank the thumbnails the driver is checking their photos with.
    useEffect(
        () => () => {
            pages.forEach((page) => URL.revokeObjectURL(page.preview));
        },
        [pages]
    );

    const authHeader = useCallback(
        () => ({ access_token: accessToken, Authorization: `Bearer ${accessToken}` }),
        [accessToken]
    );

    const findLoad = useCallback(async () => {
        const wanted = (searchText || "").trim();

        // The button stays enabled even with nothing typed. A disabled button gives a driver no reason
        // for why it will not work; a message does.
        if (!wanted) {
            setLoad(null);
            setMyLoads(null);
            setError(MESSAGES.BOL_ENTER_NUMBER);
            return;
        }

        setSearching(true);
        setError("");
        setMyLoads(null);

        try {
            const response = await axios.get(
                `${window._env_.REACT_APP_APIS}/${data.FM.DriverBol.FindDriverBol}`,
                { params: { searchText: wanted }, headers: authHeader() }
            );

            if (response.data && response.data.Found) {
                setLoad(response.data.Record);
            } else {
                setLoad(null);
                setError(MESSAGES.BOL_LOAD_NOT_FOUND);
            }
        } catch {
            setLoad(null);
            setError(MESSAGES.BOL_LOAD_LOOKUP_FAILED);
        } finally {
            setSearching(false);
        }
    }, [searchText, authHeader]);

    const showMyLoads = useCallback(async () => {
        setSearching(true);
        setError("");

        try {
            const response = await axios.get(
                `${window._env_.REACT_APP_APIS}/${data.FM.DriverBol.GetMyLoads}`,
                { headers: authHeader() }
            );
            setMyLoads(Array.isArray(response.data) ? response.data : []);
        } catch {
            setError(MESSAGES.BOL_LOAD_LOOKUP_FAILED);
        } finally {
            setSearching(false);
        }
    }, [authHeader]);

    const addPages = useCallback(async (event) => {
        const chosen = Array.from(event.target.files || []);
        // Cleared straight away so picking the same photo twice in a row still raises a change event.
        event.target.value = "";
        if (chosen.length === 0) return;

        const room = MAX_PAGES - pages.length;
        if (room <= 0) {
            setError(MESSAGES.BOL_TOO_MANY_PAGES);
            return;
        }

        const accepted = await Promise.all(chosen.slice(0, room).map(downscale));

        setPages((previous) => [
            ...previous,
            ...accepted.map((blob, index) => ({
                id: `${Date.now()}-${index}`,
                blob,
                name: chosen[index]?.name || `page-${previous.length + index + 1}.jpg`,
                preview: URL.createObjectURL(blob),
            })),
        ]);
        setError("");
    }, [pages.length]);

    const removePage = useCallback((id) => {
        setPages((previous) => {
            const dropped = previous.find((page) => page.id === id);
            if (dropped) URL.revokeObjectURL(dropped.preview);
            return previous.filter((page) => page.id !== id);
        });
    }, []);

    const sendBol = useCallback(async () => {
        if (!load || pages.length === 0) return;

        setSending(true);
        setError("");

        const body = new FormData();
        body.append("freightBillRowId", load.RowId);
        if (note.trim()) body.append("note", note.trim());
        pages.forEach((page) => body.append("pages", page.blob, page.name));

        try {
            // No Content-Type: the body is FormData and the endpoint binds it with [FromForm], so axios
            // has to set multipart/form-data with its own boundary.
            //
            // No success check on the body either: a save that did not happen now answers 400 or 500, so
            // axios rejects and the catch below handles it. Reaching this line means the pages are stored.
            await axios.post(
                `${window._env_.REACT_APP_APIS}/${data.FM.DriverBol.SendBol}`,
                body,
                { headers: authHeader() }
            );

            // The photos are kept until the driver starts again. A send that failed must never cost them
            // the pictures - they are standing somewhere with no second chance to take them.
            pages.forEach((page) => URL.revokeObjectURL(page.preview));
            setPages([]);
            setNote("");
            setSent(true);
        } catch {
            setError(MESSAGES.BOL_SEND_FAILED);
        } finally {
            setSending(false);
        }
    }, [load, pages, note, authHeader]);

    const startAgain = useCallback(() => {
        pages.forEach((page) => URL.revokeObjectURL(page.preview));
        setPages([]);
        setNote("");
        setLoad(null);
        setMyLoads(null);
        setSearchText("");
        setSent(false);
        setError("");
    }, [pages]);

    const origin = load ? place(load.ShipperName, load.ShipperCity, load.ShipperState) : null;
    const destination = load ? place(load.ConsigneeName, load.ConsigneeCity, load.ConsigneeState) : null;
    const hasPaperwork = load && load.BolPageCount > 0;

    const step = (number, text) => (
        <div className="sendbol-step">
            <span className="sendbol-step__number">{number}</span>
            <span className="sendbol-step__text">{text}</span>
        </div>
    );

    return (
        // Owns its own scrolling. The shell's content pane is overflow:hidden at a fixed height and the
        // data-entry containers inside it carry no overflow rule of their own, so a page taller than the
        // viewport simply gets clipped - which is what hid the note field and the Send button. The grid
        // pages solve the same problem in searchPage.css; this is the form equivalent.
        <div className="sendbol-root">
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            visible={searching || sending}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />

                        <Toolbar className="header">
                            <Item location="before">
                                <div className="title">
                                    <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>
                                        {MESSAGES.BOL_PAGE_TITLE}
                                    </h6>
                                </div>
                            </Item>
                            <Item location="after" visible={!isMobileView && load != null && !sent}>
                                <Button
                                    className="accordianButton"
                                    text={MESSAGES.BOL_SEND}
                                    type="default"
                                    disabled={pages.length === 0 || sending}
                                    onClick={sendBol}
                                />
                            </Item>
                            <Item location="after" visible={!isMobileView && (load != null || sent)}>
                                <Button className="accordianButton" text={MESSAGES.RESET} onClick={startAgain} />
                            </Item>
                        </Toolbar>

                        <div className="AccordionItemBody">
                            <div className="sendbol">
                                {sent && (
                                    <div className="sendbol-card sendbol-sent">
                                        <div className="sendbol-sent__tick">&#10003;</div>
                                        <p className="sendbol-sent__text">{MESSAGES.BOL_SENT_CONFIRMATION}</p>
                                        <Button
                                            text={MESSAGES.BOL_SEND_ANOTHER}
                                            type="default"
                                            width="100%"
                                            height={48}
                                            onClick={startAgain}
                                        />
                                    </div>
                                )}

                                {!sent && !load && (
                                    <div className="sendbol-card">
                                        {step(1, MESSAGES.BOL_STEP_FIND)}

                                        <TextBox
                                            value={searchText}
                                            placeholder={MESSAGES.BOL_SEARCH_PLACEHOLDER}
                                            stylingMode="outlined"
                                            labelMode="static"
                                            height={48}
                                            onValueChanged={(e) => setSearchText(e.value)}
                                            onEnterKey={findLoad}
                                        />

                                        <Button
                                            text={MESSAGES.BOL_FIND_LOAD}
                                            type="default"
                                            icon="search"
                                            width="100%"
                                            height={48}
                                            className="sendbol-spaced"
                                            onClick={findLoad}
                                        />

                                        <div className="sendbol-hint">{MESSAGES.BOL_SEARCH_HINT}</div>
                                        {error && <div className="sendbol-error">{error}</div>}

                                        {/* For a load carrying no number the driver can read off
                                            paperwork - the hourly transfer moves between terminals. */}
                                        <Button
                                            text={MESSAGES.BOL_NO_NUMBER}
                                            stylingMode="text"
                                            type="default"
                                            className="sendbol-linkbutton"
                                            onClick={showMyLoads}
                                        />

                                        {myLoads && myLoads.length === 0 && (
                                            <div className="sendbol-hint">{MESSAGES.BOL_NO_LOADS_FOR_DRIVER}</div>
                                        )}

                                        {myLoads && myLoads.length > 0 && (
                                            <div className="sendbol-loads">
                                                {myLoads.map((option) => (
                                                    <button
                                                        key={option.RowId}
                                                        type="button"
                                                        className="sendbol-load-option"
                                                        onClick={() => { setLoad(option); setMyLoads(null); }}
                                                    >
                                                        <div className="sendbol-load__place">
                                                            {MESSAGES.BOL_FREIGHT_BILL}{" "}
                                                            {(option.TransactionNumber || "").trim()}
                                                        </div>
                                                        <div className="sendbol-load__where">
                                                            {[option.ConsigneeName, formatDate(option.DeliveryDate)]
                                                                .map((v) => (v || "").toString().trim())
                                                                .filter(Boolean)
                                                                .join(" · ")}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!sent && load && (
                                    <>
                                        <div className="sendbol-card">
                                            {step(2, MESSAGES.BOL_STEP_CONFIRM)}

                                            <div className="sendbol-load__heading">
                                                <span className="sendbol-load__number">
                                                    {MESSAGES.BOL_FREIGHT_BILL}{" "}
                                                    {(load.TransactionNumber || "").trim()}
                                                </span>
                                                <span
                                                    className={`sendbol-load__status${
                                                        hasPaperwork ? " sendbol-load__status--received" : ""
                                                    }`}
                                                >
                                                    {hasPaperwork
                                                        ? MESSAGES.BOL_STATUS_RECEIVED
                                                        : MESSAGES.BOL_STATUS_WAITING}
                                                </span>
                                            </div>

                                            {load.BillOfLadingNumber && (
                                                <div className="sendbol-load__where">
                                                    {MESSAGES.BOL_NUMBER} {load.BillOfLadingNumber.trim()}
                                                </div>
                                            )}

                                            <div className="sendbol-load__stops">
                                                <div className="sendbol-load__stop">
                                                    <div className="sendbol-load__place">{origin.name}</div>
                                                    <div className="sendbol-load__where">
                                                        {[origin.where, formatDate(load.LoadDate)]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                    </div>
                                                </div>
                                                <div className="sendbol-load__stop">
                                                    <div className="sendbol-load__place">{destination.name}</div>
                                                    <div className="sendbol-load__where">
                                                        {[destination.where, formatDate(load.DeliveryDate)]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="sendbol-load__facts">
                                                <div>
                                                    <div className="sendbol-load__label">{MESSAGES.BOL_PALLETS}</div>
                                                    <div>{load.Pallets ?? "-"}</div>
                                                </div>
                                                <div>
                                                    <div className="sendbol-load__label">{MESSAGES.BOL_WEIGHT}</div>
                                                    <div>{load.Weight ?? "-"}</div>
                                                </div>
                                                <div>
                                                    <div className="sendbol-load__label">{MESSAGES.BOL_COMMODITY}</div>
                                                    <div>{(load.Commodity || "").trim() || "-"}</div>
                                                </div>
                                            </div>

                                            <Button
                                                text={MESSAGES.BOL_NOT_MY_LOAD}
                                                stylingMode="text"
                                                type="default"
                                                className="sendbol-linkbutton"
                                                onClick={startAgain}
                                            />
                                        </div>

                                        <div className="sendbol-card">
                                            {step(3, MESSAGES.BOL_STEP_PHOTOGRAPH)}

                                            {/* `capture` opens the camera directly; the second input omits
                                                it so the driver can pick a photo already taken. */}
                                            <input
                                                ref={cameraRef}
                                                className="sendbol-hidden-input"
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                multiple
                                                onChange={addPages}
                                            />
                                            <input
                                                ref={libraryRef}
                                                className="sendbol-hidden-input"
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={addPages}
                                            />

                                            <Button
                                                text={MESSAGES.BOL_TAKE_PHOTO}
                                                type="default"
                                                icon="photo"
                                                width="100%"
                                                height={48}
                                                onClick={() => cameraRef.current && cameraRef.current.click()}
                                            />
                                            <Button
                                                text={MESSAGES.BOL_CHOOSE_PHOTO}
                                                type="normal"
                                                icon="image"
                                                width="100%"
                                                height={48}
                                                className="sendbol-spaced"
                                                onClick={() => libraryRef.current && libraryRef.current.click()}
                                            />

                                            {pages.length > 0 && (
                                                <div className="sendbol-pages">
                                                    {pages.map((page, index) => (
                                                        <div key={page.id} className="sendbol-page-thumb">
                                                            <img
                                                                src={page.preview}
                                                                alt={`${MESSAGES.BOL_PAGE} ${index + 1}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="sendbol-page-thumb__remove"
                                                                aria-label={MESSAGES.BOL_REMOVE_PAGE}
                                                                onClick={() => removePage(page.id)}
                                                            >
                                                                &times;
                                                            </button>
                                                            <div className="sendbol-page-thumb__caption">
                                                                {MESSAGES.BOL_PAGE} {index + 1}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="sendbol-hint">{MESSAGES.BOL_PHOTO_HINT}</div>

                                            <TextArea
                                                value={note}
                                                placeholder={MESSAGES.BOL_NOTE_PLACEHOLDER}
                                                stylingMode="outlined"
                                                height={88}
                                                className="sendbol-spaced"
                                                onValueChanged={(e) => setNote(e.value)}
                                            />

                                            {error && <div className="sendbol-error">{error}</div>}

                                            {/* On a phone the action belongs with the form, not in a
                                                toolbar the driver has scrolled away from. */}
                                            <Button
                                                text={MESSAGES.BOL_SEND}
                                                type="default"
                                                icon="upload"
                                                width="100%"
                                                height={48}
                                                className="sendbol-spaced"
                                                visible={isMobileView}
                                                disabled={pages.length === 0 || sending}
                                                onClick={sendBol}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendBol;
